import { useCallback } from 'react'

import { ClientScenario } from '@cash-web/protos/squareup/franklin/api/client_scenario.pb'
import { BlockerAction } from '@cash-web/protos/squareup/franklin/blocker_action.pb'
import { useLogCashWebCdfEvent } from '@cash-web/shared-feature-analytics'
import { getBlockerDataDetail, isTerminalBlocker } from '@cash-web/shared-flow-data-access-context'
import { RouteInfo } from '@cash-web/shared-util-route-info'
import {
  BlockerFlowInteractCancelFlow,
  BlockerFlowInteractCompleteFlow,
  BlockerFlowInteractSkipBlocker,
  BlockerFlowInteractStartFlow,
  BlockerFlowInteractSubmitBlocker,
  BlockerFlowInteractTapBlockerAction,
  BlockerFlowInteractTapBlockerActionParams,
  BlockerFlowInteractViewBlocker,
  BlockerFlowInteractViewBlockerResponse,
  BlockerFlowInteractViewBlockerResponseStatus,
} from '@squareup/customer-data-events-web'

import { useLogBlockerErrorEvent, useLogBlockerStartEvent, useLogBlockerSuccessEvent } from './analytics'
import { blockerActionToCDFBlockerAction } from './blockerActionToCDFBlockerAction'
import { FlowState } from './flowReducer'

const getAnalyticsClientScenario = (state: FlowState) => {
  const isPlasma = state.clientScenario === ClientScenario.PLASMA
  const hasOriginatingFlowType = !!state.originatingFlowType
  if (isPlasma && hasOriginatingFlowType) {
    return `${ClientScenario.PLASMA}_${state.originatingFlowType}`
  }
  return state.clientScenario ?? state.trackingFlowName
}

export const useFlowLogging = () => {
  const logCdfEvent = useLogCashWebCdfEvent()

  // old blocker events
  // TODO: remove after validating new events
  const logBlockerSuccessEvent = useLogBlockerSuccessEvent()
  const logBlockerStartEvent = useLogBlockerStartEvent()
  const logBlockerErrorEvent = useLogBlockerErrorEvent()

  const logStartFlow = useCallback(
    (state: FlowState) => {
      logCdfEvent(
        BlockerFlowInteractStartFlow({
          flowToken: state.flowToken,
          clientScenario: getAnalyticsClientScenario(state),
          sourceScreen: typeof window !== 'undefined' ? window.location.href : '',
          originatingFlowType: state.originatingFlowType,
        })
      )
    },
    [logCdfEvent]
  )

  const logCompleteFlow = useCallback(
    (state: FlowState) => {
      logCdfEvent(
        BlockerFlowInteractCompleteFlow({
          flowToken: state.flowToken,
          clientScenario: getAnalyticsClientScenario(state),
          lastBlockerId: state.activeBlockerDescriptor?.id,
          flowDurationMs: state.flowStartTime ? Date.now() - state.flowStartTime : undefined,
          finalPath: state.path.join('->'),
          uniqueFinalPath: [...new Set(state.path)].join('->'),
          originatingFlowType: state.originatingFlowType,
        })
      )
    },
    [logCdfEvent]
  )
  const logCancelFlow = useCallback(
    (state: FlowState) => {
      logCdfEvent(
        BlockerFlowInteractCancelFlow({
          flowToken: state.flowToken,
          clientScenario: getAnalyticsClientScenario(state),
          lastBlockerId: state.activeBlockerDescriptor?.id,
          flowDurationMs: state.flowStartTime ? Date.now() - state.flowStartTime : undefined,
          originatingFlowType: state.originatingFlowType,
        })
      )
    },
    [logCdfEvent]
  )
  // this should log when the blocker is submitted to the server
  const logBlockerSubmit = useCallback(
    (state: FlowState) => {
      const blockerData = getBlockerDataDetail(state.activeBlockerDescriptor ?? {})
      logCdfEvent(
        BlockerFlowInteractSubmitBlocker({
          blockerId: blockerData.id,
          flowToken: state.flowToken,
          clientScenario: getAnalyticsClientScenario(state),
          blockerType: blockerData.trackingName,
          inputDurationMs: state.blockerViewStartTime ? Date.now() - state.blockerViewStartTime : undefined,
          originatingFlowType: state.originatingFlowType,
        })
      )
    },
    [logCdfEvent]
  )

  const logBlockerView = useCallback(
    (state: FlowState) => {
      const blockerData = getBlockerDataDetail(state.activeBlockerDescriptor ?? {})
      //TODO: remove event after validating new events
      logBlockerStartEvent({
        clientScenario: getAnalyticsClientScenario(state),
        blockerName: blockerData.trackingName,
        blockerDescriptorId: blockerData?.id,
        flowToken: state.flowToken,
      })
      logCdfEvent(
        BlockerFlowInteractViewBlocker({
          blockerId: blockerData.id,
          flowToken: state.flowToken,
          clientScenario: getAnalyticsClientScenario(state),
          blockerType: blockerData.trackingName,
          originatingFlowType: state.originatingFlowType,
        })
      )
    },
    [logBlockerStartEvent, logCdfEvent]
  )

  const logBlockerSkip = useCallback(
    (state: FlowState) => {
      const blockerData = getBlockerDataDetail(state.activeBlockerDescriptor ?? {})
      logCdfEvent(
        BlockerFlowInteractSkipBlocker({
          flowToken: state.flowToken,
          clientScenario: getAnalyticsClientScenario(state),
          blockerId: blockerData.id,
          blockerType: blockerData.trackingName,
          originatingFlowType: state.originatingFlowType,
        })
      )
    },
    [logCdfEvent]
  )

  const logFlowResponse = useCallback(
    ({
      prev,
      next,
      status,
      errorCode,
      errorMessage,
    }: {
      prev: FlowState
      next: FlowState
      status: BlockerFlowInteractViewBlockerResponseStatus
      errorCode?: string
      errorMessage?: string
    }) => {
      const blockerData = getBlockerDataDetail(next.activeBlockerDescriptor ?? {})
      // TODO: remove event after validating new events
      const legacyEvent = {
        clientScenario: next.clientScenario ?? next.trackingFlowName,
        blockerName: blockerData.trackingName,
        blockerDescriptorId: blockerData.id,
        flowToken: next.flowToken,
        flowName: next.trackingFlowName,
        terminalBlocker: isTerminalBlocker(next.responseContext?.scenario_plan),
      }
      if (errorCode || errorMessage) {
        // legacy error event
        logBlockerErrorEvent(legacyEvent)
      } else {
        // legacy success event
        logBlockerSuccessEvent(legacyEvent)
      }
      logCdfEvent(
        BlockerFlowInteractViewBlockerResponse({
          flowToken: next.flowToken,
          clientScenario: next.clientScenario ?? next.trackingFlowName,
          blockerId: blockerData.id,
          blockerType: blockerData.trackingName,
          requestDurationMs: prev.blockerSubmitStartTime ? prev.blockerSubmitStartTime - Date.now() : undefined,
          originatingFlowType: next.originatingFlowType,
          status,
          errorCode,
          errorMessage,
        })
      )
    },
    [logBlockerErrorEvent, logBlockerSuccessEvent, logCdfEvent]
  )

  const getRouteInfoUrl = (routeInfo: RouteInfo) => {
    switch (routeInfo.type) {
      case 'journey':
        return undefined
      default:
        return routeInfo.url
    }
  }
  const getRouteClientScenario = (routeInfo: RouteInfo) => {
    switch (routeInfo.type) {
      case 'journey':
        // Extract clientScenario from journey paths like '/dl/scenario/RESET_PASSCODE'
        if (routeInfo.journey.startsWith('/dl/scenario/')) {
          return routeInfo.journey.replace('/dl/scenario/', '')
        }
        // For flow journeys like '/dl/flow/TAX_UPGRADE', we don't have a clientScenario
        // but we can return the flow name for consistency
        if (routeInfo.journey.startsWith('/dl/flow/')) {
          return routeInfo.journey.replace('/dl/flow/', '').split('?')[0]
        }
        return undefined
      default:
        return undefined
    }
  }
  const logLoadUrl = useCallback(
    ({ state, routeInfo, trackingName }: { state: FlowState; routeInfo: RouteInfo; trackingName?: string }) => {
      const blockerData = getBlockerDataDetail(state.activeBlockerDescriptor ?? {})
      logCdfEvent(
        BlockerFlowInteractTapBlockerAction({
          actionId: routeInfo.type,
          actionText: trackingName ?? getRouteInfoUrl(routeInfo),
          flowToken: state.flowToken,
          clientScenario: getRouteClientScenario(routeInfo),
          blockerId: blockerData.id,
          // this is not a BlockerAction, but we want to treat it like one, this gives us the ability to track this.
          blockerAction: 'LOAD_URL' as BlockerFlowInteractTapBlockerActionParams['blockerAction'],
          blockerType: blockerData.trackingName === 'unknown' ? undefined : blockerData.trackingName,
          originatingFlowType: state.originatingFlowType,
        })
      )
    },
    [logCdfEvent]
  )

  const logExecuteBlockerAction = useCallback(
    ({ state, action, trackingName }: { state: FlowState; action: BlockerAction; trackingName?: string }) => {
      const blockerData = getBlockerDataDetail(state.activeBlockerDescriptor ?? {})
      const blockerAction = Object.keys(action).filter(
        key => !['text', 'localized_text', 'icon', 'haptic_feedback_action'].includes(key)
      )[0] as Exclude<keyof BlockerAction, 'text' | 'localized_text' | 'icon' | 'haptic_feedback_action'>

      const cdfBlockerAction = blockerActionToCDFBlockerAction(blockerAction)

      logCdfEvent(
        BlockerFlowInteractTapBlockerAction({
          actionId: action.submit_action?.id,
          actionText: trackingName,
          flowToken: state.flowToken,
          clientScenario: getAnalyticsClientScenario(state),
          blockerId: blockerData.id,
          blockerType: blockerData.trackingName === 'unknown' ? undefined : blockerData.trackingName,
          blockerAction: cdfBlockerAction,
          originatingFlowType: state.originatingFlowType,
        })
      )
    },
    [logCdfEvent]
  )

  return {
    logStartFlow,
    logCompleteFlow,
    logCancelFlow,
    logBlockerSubmit,
    logFlowResponse,
    logBlockerView,
    logBlockerSkip,
    logLoadUrl,
    logExecuteBlockerAction,
  }
}
